<template>
  <div class="c-image">
    <img
      ref="imgRef"
      v-if="!error"
      class="c-image__img"
      @load="onLoad"
      @error="onError"
      :src="src"
      :alt="alt"
      :loading="lazy ? 'lazy' : 'eager'"
      :style="{
        objectFit: fit,
        objectPosition: position
      }"
    >
    <!-- 加载状态 -->
    <div
      v-if="loading"
      class="c-image__loading"
      :style="{
        backgroundColor: loadingBackgroundColor
      }"
      @click="onLoadingClick"
    />

    <!-- 错误状态 -->
    <div
      v-if="error"
      class="c-image-error"
      @click.stop="onRetry"
    >
      <Icon
        class="c-image-error__icon"
        name="picture-error"
      />
      <p class="c-image-error__description">
        Image not available
      </p>
      <p class="c-image-error__reload">
        Re-loading
      </p>
    </div>
  </div>
</template>

<script setup>
import { Icon } from '@/components/Icon'
import { getColor } from './colors'

const props = defineProps({
  src: {
    type: String,
    default: null
  },
  alt: {
    type: String,
    default: null
  },
  lazy: Boolean,
  fit: { type: String, default: 'contain' },
  position: { type: String, default: 'center' }
})

const emit = defineEmits(['load', 'error', 'loadingClick'])

const imgRef = useTemplateRef('imgRef')
const loading = ref(true)
const loadingBackgroundColor = getColor()
const error = ref(false)

watch(
  () => props.src,
  () => {
    error.value = false
    loading.value = true
    /**
     * 图片缓存检查，如果图片已存在缓存，则不触发 loading 样式，防止出现闪屏现象
     */
    if (imgRef.value?.complete) {
      loading.value = false
    }
  }
)

onMounted(() => {
  /**
   * 图片缓存检查，如果图片已存在缓存，则不触发 loading 样式，防止出现闪屏现象
   */
  if (imgRef.value?.complete) {
    loading.value = false
  }
})

const onLoad = (event) => {
  loading.value = false
  emit('load', event)
}

const onError = (event) => {
  error.value = true
  loading.value = false
  emit('error', event)
}

const onRetry = () => {
  error.value = false
  loading.value = true
}

const onLoadingClick = (event) => {
  emit('loadingClick', event)
}
</script>

<style lang="less">
.c-image {
  display: inline-block;
  position: relative;

  &__img, &__loading, &-error {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: inherit;
  }

  &__loading, &-error {
    position: absolute;
    top: 0;
    left: 0;
  }

  &-error {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: #F8F8F8;
    font-family: mon-500;
    font-size: 12px;
    cursor: pointer;
    user-select: none;
    text-align: center;
    container-type: inline-size;
    container-name: image;

    @container image (width <= 100px) {
      text-align: left;
      &__icon {
        width: 40%;
        height: 40%;
      }

      &__description, &__reload {
        display: none;
      }
    }

    &__icon {
      font-size: 48px;
      color: #B2B7C2;
    }

    &__description {
      margin-top: 4px;
      font-family: mon-500;
      font-size: 12px;
      line-height: 16px;
      color: #B2B7C2;
    }

    &__reload {
      margin-top: 8px;
      font-family: mon-500;
      font-size: 12px;
      line-height: 16px;
      text-decoration: underline;
      color: #232323;
    }
  }
}
</style>
